import { useQuery } from '@tanstack/react-query';
import { api } from '@xtnd/web/shared/api';

export interface BankTransactionsParams {
    matterId: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
}

export interface BankTransaction {
    id: number;
    type: string;
    date: string;
    amount: number;
    currency: string;
    description: string;
    transaction_type?: string;
    current_account_balance?: number;
    funds_in?: number;
    funds_out?: number;
    created_at?: string;
    updated_at?: string;
}

export interface BankTransactionsResponse {
    transactions: BankTransaction[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
    note?: string;
}

export const bankTransactionKeys = {
    all: ['bank-transactions'] as const,
    lists: () => [...bankTransactionKeys.all, 'list'] as const,
    list: (params: BankTransactionsParams) => [...bankTransactionKeys.lists(), params] as const,
};

export function useBankTransactions(params: BankTransactionsParams) {
    // Validate matterId before making any query
    const isValidMatterId = !!params.matterId && params.matterId.trim() !== '' && params.matterId !== 'Unknown' && !isNaN(Number(params.matterId));

    return useQuery({
        queryKey: bankTransactionKeys.list(params),
        queryFn: async (): Promise<BankTransactionsResponse> => {
            // Convert matterId to number for API validation
            const matterIdNum = parseInt(params.matterId, 10);
            if (isNaN(matterIdNum)) {
                throw new Error(`Invalid matterId: ${params.matterId}`);
            }

            // Build query string
            const queryParams = new URLSearchParams({
                matterId: matterIdNum.toString(),
                page: (params.page || 1).toString(),
                limit: (params.limit || 50).toString(),
                sortBy: params.sortBy || 'date',
                sortDirection: params.sortDirection || 'desc',
            });

            const response = await api.get<BankTransactionsResponse>(`/clio/bank-transactions?${queryParams.toString()}`);
            return response;
        },
        enabled: isValidMatterId,
        staleTime: 2 * 60 * 1000, // 2 minutes
    });
}

